/**
 * @file src/pages/authentication-modal/useLogin.js
 * @description React Query mutation hook for user login.
 *
 * Rules:
 *  - No Redux
 *  - No direct axios
 *  - No navigation
 *  - No invite acceptance here
 *  - AuthenticationModal handles routing and pending invite acceptance
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { loginUser } from "api/auth.api";
import { QUERY_KEYS } from "config/queryKeys";

/**
 * Mutation hook for logging in a user.
 *
 * On success:
 *  - Seeds auth.me cache
 *  - Invalidates company/invitation-related state that may depend on login
 *
 * Caller handles navigation:
 *  mutate(credentials, {
 *    onSuccess: (response) => {},
 *    onError: (error) => {},
 *  });
 */
export const useLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ email, password }) => loginUser({ email, password }),

    onSuccess: (response) => {
      if (response?.user) {
        queryClient.setQueryData(QUERY_KEYS.auth.me(), response);
      }

      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.companies.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invitations.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.memberships.all });
    },
  });
};
